import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useMissingMedia } from '../utils/missingMedia';
import {
  ArrowUpDown,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Download,
  FolderTree,
  Image as ImageIcon,
  Inbox,
  LayoutGrid,
  List,
  LucideIcon,
  PanelLeft,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { DashboardLayout } from '../components/dashboard';
import { mediaService } from '../services/mediaService';
import { MediaAsset } from '../types/media';
import { EntityTreeSidebar, TreeSelection } from '../components/library/EntityTreeSidebar';
import { ConfirmModal } from '../components/common/ConfirmModal';
import { AuthImage } from '../components/media/AuthImage';
import { MediaViewer } from '../components/media/MediaViewer';
import { MediaTypeTile } from '../components/media/MediaTypeIcon';
import { FileUpload } from '../components/media';
import { useAuth } from '../contexts/AuthContext';
import { useDebounce } from '../hooks/useDebounce';
import { asNumber, asOneOf, useUrlState, useUrlWriter } from '../hooks/useUrlState';
import {
  Button,
  Card,
  Dialog,
  DialogHeader,
  EmptyState,
  EntityIcon,
  IconButton,
  Menu,
  Page,
  PageHeader,
  SearchInput,
  SegmentedControl,
  Select,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui';

type ViewMode = 'grid' | 'list';
type TypeFilter = '' | 'image' | 'video' | 'audio' | 'document';
type SortOption = 'newest' | 'oldest' | 'largest' | 'smallest' | 'az';

const TYPE_ITEMS: { value: TypeFilter; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'image', label: 'Images' },
  { value: 'video', label: 'Videos' },
  { value: 'audio', label: 'Audio' },
  { value: 'document', label: 'Docs' },
];

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'largest', label: 'Largest' },
  { value: 'smallest', label: 'Smallest' },
  { value: 'az', label: 'A–Z' },
];

/* One codec per parameter, made once: the URL state reads them on every render */
const SCOPE = asOneOf<TreeSelection['type']>([
  'all',
  'stakeholder',
  'organization',
  'stakeholders',
  'organizations',
  'unassigned',
]);
const TYPE = asOneOf(TYPE_ITEMS.map((option) => option.value));
const SORT = asOneOf(SORT_OPTIONS.map((option) => option.value));
const VIEW = asOneOf<ViewMode>(['grid', 'list']);
const PAGE = asNumber(1);
/** The page sizes the picker offers; anything else falls back to the default */
const PAGE_SIZE = asNumber(1, 96);

const GROUP_LABELS: Record<string, string> = {
  all: 'All files',
  stakeholders: 'Stakeholders',
  organizations: 'Organizations',
  unassigned: 'Uncategorized',
};

function sortToParams(sort: SortOption): { sortBy: string; sortOrder: 'ASC' | 'DESC' } {
  switch (sort) {
    case 'newest': return { sortBy: 'createdAt', sortOrder: 'DESC' };
    case 'oldest': return { sortBy: 'createdAt', sortOrder: 'ASC' };
    case 'largest': return { sortBy: 'size', sortOrder: 'DESC' };
    case 'smallest': return { sortBy: 'size', sortOrder: 'ASC' };
    case 'az': return { sortBy: 'name', sortOrder: 'ASC' };
  }
}

/** Empty icon slot so unchecked menu items line up with the checked one */
const Blank = (({ className }: { className?: string }) => <span className={className} aria-hidden="true" />) as unknown as LucideIcon;

/** Round selection mark in the corner of a tile, as in Photos */
const SelectMark: React.FC<{ selected: boolean; className?: string }> = ({ selected, className = '' }) => (
  <span
    aria-hidden="true"
    className={`inline-flex h-[22px] w-[22px] items-center justify-center rounded-full ring-[1.5px] ring-white transition-colors ${
      selected ? 'bg-primary-600' : 'bg-black/20 backdrop-blur-sm'
    } ${className}`}
  >
    {selected && <Check className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />}
  </span>
);

export const MediaPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');

  // What you are looking at — which folder, the filters, which page — is in the
  // address bar, so coming back or sending the link shows the same shelf
  const write = useUrlWriter();
  const [scope] = useUrlState<TreeSelection['type']>('scope', 'all', SCOPE);
  const [scopeId] = useUrlState('scopeId', '');
  const selection = useMemo<TreeSelection>(
    () => ({ type: scope, id: scopeId || undefined }),
    [scope, scopeId],
  );

  /** Narrowing what is listed starts again at page one, in one move */
  const refilter = useCallback(
    (changes: Record<string, string | null>) => write({ ...changes, page: null }),
    [write],
  );

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [treeSheetOpen, setTreeSheetOpen] = useState(false);

  const [allAssets, setAssets] = useState<MediaAsset[]>([]);
  const missingMedia = useMissingMedia();
  // A file the server says is gone is not listed: nothing can be done with it
  const assets = useMemo(() => allAssets.filter((a) => !missingMedia(a.id)), [allAssets, missingMedia]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [q] = useUrlState('q', '');
  const [typeFilter] = useUrlState<TypeFilter>('type', '', TYPE);
  const [sort] = useUrlState<SortOption>('sort', 'newest', SORT);
  const [page, setPage] = useUrlState('page', 1, PAGE);
  const [limit] = useUrlState('limit', 24, PAGE_SIZE);
  const [viewMode, setViewMode] = useUrlState<ViewMode>('view', 'grid', VIEW);

  // The box answers every keystroke; the URL catches up once typing stops
  const [searchInput, setSearchInput] = useState(q);
  const settledSearch = useDebounce(searchInput, 300);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<MediaAsset | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [viewerAsset, setViewerAsset] = useState<MediaAsset | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const [entityCounts, setEntityCounts] = useState<Record<string, number>>({});
  const [totalMediaCount, setTotalMediaCount] = useState(0);

  // On mount: link orphan business card / profile media, then load counts
  const loadCounts = useCallback(() => {
    mediaService.getCounts().then(res => {
      const counts = res.counts || {};
      counts['unassigned'] = res.unassigned || 0;
      setEntityCounts(counts);
      setTotalMediaCount(res.total || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    // Load counts immediately
    loadCounts();
    // Fire-and-forget: link any orphan media in background, then refresh counts
    mediaService.linkBusinessCards()
      .then(res => { if (res.linked > 0) loadCounts(); })
      .catch(() => {});
  }, [loadCounts]);

  const loadAssets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { sortBy, sortOrder } = sortToParams(sort);

      const filter: any = {
        page,
        limit,
        sortBy,
        sortOrder,
        search: q || undefined,
        type: typeFilter || undefined,
      };

      if (selection.type === 'stakeholder' && selection.id) {
        filter.stakeholderId = selection.id;
      } else if (selection.type === 'organization' && selection.id) {
        filter.organizationId = selection.id;
      } else if (selection.type === 'unassigned') {
        filter.unassigned = true;
      }

      const resp = await mediaService.getMediaAssetsPaginated(filter);
      let data = resp.data || [];

      // Client-side filter for entity-type groups
      if (selection.type === 'stakeholders') {
        data = data.filter(a => a.stakeholderId);
      } else if (selection.type === 'organizations') {
        data = data.filter(a => a.organizationId);
      }

      setAssets(data);
      setTotal(selection.type === 'stakeholders' || selection.type === 'organizations' ? data.length : resp.total || 0);
    } catch {
      setError('Failed to load media assets');
    } finally {
      setLoading(false);
    }
  }, [page, limit, sort, typeFilter, q, selection]);

  useEffect(() => { loadAssets(); }, [loadAssets]);

  useEffect(() => {
    if (settledSearch !== q) refilter({ q: settledSearch || null });
  }, [settledSearch, q, refilter]);

  const flash = useCallback((message: string) => {
    setActionMessage(message);
    setTimeout(() => setActionMessage(null), 3000);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await mediaService.deleteMediaAsset(deleteTarget.id);
      setAssets(prev => prev.filter(a => a.id !== deleteTarget.id));
      setTotal(prev => prev - 1);
      setSelectedIds(prev => { const n = new Set(prev); n.delete(deleteTarget.id); return n; });
      flash('File deleted');
      loadCounts();
    } catch {
      flash('Failed to delete file');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, loadCounts, flash]);

  const handleBulkDelete = useCallback(async () => {
    const toDelete = assets.filter(a => selectedIds.has(a.id));
    setBulkDeleting(true);
    for (const a of toDelete) {
      try { await mediaService.deleteMediaAsset(a.id); } catch {}
    }
    setBulkDeleting(false);
    setBulkConfirmOpen(false);
    setSelectedIds(new Set());
    setSelectMode(false);
    loadAssets();
    loadCounts();
    flash(`Deleted ${toDelete.length} file(s)`);
  }, [selectedIds, assets, loadAssets, loadCounts, flash]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === assets.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(assets.map(a => a.id)));
  };

  const endSelection = () => {
    setSelectedIds(new Set());
    setSelectMode(false);
  };

  /** Names the tree hands over, kept so the breadcrumb can read them back */
  const labels = useRef<Record<string, string>>({});

  const selectEntity = (next: TreeSelection) => {
    if (next.id && next.name) labels.current[next.id] = next.name;
    refilter({ scope: next.type === 'all' ? null : next.type, scopeId: next.id ?? null });
    setSelectedIds(new Set());
    setTreeSheetOpen(false);
  };

  const openGroup = (type: TreeSelection['type']) => selectEntity({ type });

  /**
   * A name is a label, not view state, so the URL carries only the id and the
   * name is read back from the files that belong to the entity. One seen once
   * is remembered, so a filter that empties the list — or an id that matches
   * nothing at all — still reads as the folder you opened.
   */
  const scopeName = useMemo(() => {
    if (!scopeId) return '';
    const owner = allAssets.find(
      (a) => (scope === 'stakeholder' ? a.stakeholderId : a.organizationId) === scopeId,
    );
    const name =
      scope === 'stakeholder'
        ? `${owner?.stakeholder?.firstName || ''} ${owner?.stakeholder?.lastName || ''}`.trim()
        : owner?.organization?.name || '';
    if (name) labels.current[scopeId] = name;
    return name || labels.current[scopeId] || '';
  }, [allAssets, scope, scopeId]);

  const breadcrumb = useMemo(() => {
    const parts: { label: string; onClick?: () => void }[] = [
      { label: 'All files', onClick: () => openGroup('all') },
    ];
    if (selection.type === 'stakeholders') parts.push({ label: 'Stakeholders' });
    else if (selection.type === 'organizations') parts.push({ label: 'Organizations' });
    else if (selection.type === 'unassigned') parts.push({ label: 'Uncategorized' });
    else if (selection.type === 'stakeholder' && scopeName) {
      parts.push({ label: 'Stakeholders', onClick: () => openGroup('stakeholders') });
      parts.push({ label: scopeName });
    } else if (selection.type === 'organization' && scopeName) {
      parts.push({ label: 'Organizations', onClick: () => openGroup('organizations') });
      parts.push({ label: scopeName });
    }
    return parts;
    // openGroup is made afresh each render; the crumbs follow where we are
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, scopeName]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  // A page number past the end — an old link, or files deleted since — lists
  // nothing and hides the pager with it, so it falls back to the first page
  useEffect(() => {
    if (!loading && page > totalPages) setPage(1);
  }, [loading, page, totalPages, setPage]);

  const selecting = !!isAdmin && (selectMode || selectedIds.size > 0);
  const hasBulkBar = !!isAdmin && selectedIds.size > 0;
  const selectionLabel = scopeName || GROUP_LABELS[selection.type] || 'All files';

  const getEntityName = (a: MediaAsset) => {
    if (a.stakeholder) return `${a.stakeholder.firstName} ${a.stakeholder.lastName}`.trim();
    if (a.organization) return a.organization.name;
    return 'Uncategorized';
  };

  const entityIcon = (a: MediaAsset) =>
    a.stakeholder ? (
      <EntityIcon kind="stakeholder" size="xs" />
    ) : a.organization ? (
      <EntityIcon kind="organization" size="xs" />
    ) : (
      <EntityIcon kind="user" icon={Inbox} size="xs" />
    );

  const sortItems = SORT_OPTIONS.map(o => ({
    label: o.label,
    icon: sort === o.value ? Check : Blank,
    onSelect: () => refilter({ sort: o.value === 'newest' ? null : o.value }),
  }));

  const openOrSelect = (a: MediaAsset) => {
    if (selecting) toggleSelect(a.id);
    else setViewerAsset(a);
  };

  const renderThumb = (a: MediaAsset, compact = false) =>
    mediaService.isImageFile(a.mimeType) ? (
      <AuthImage mediaId={a.id} alt={a.originalName} className="h-full w-full object-cover" />
    ) : (
      <MediaTypeTile mimeType={a.mimeType} name={a.originalName} compact={compact} />
    );

  const meta = (
    <span className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5">
      {breadcrumb.map((b, i) => (
        <React.Fragment key={i}>
          {i > 0 && <ChevronRight className="h-3 w-3 flex-shrink-0 text-neutral-300" strokeWidth={1.75} />}
          {b.onClick && i < breadcrumb.length - 1 ? (
            <button type="button" onClick={b.onClick} className="truncate transition-colors hover:text-ink">
              {b.label}
            </button>
          ) : (
            <span className={`truncate ${breadcrumb.length > 1 ? 'text-ink' : ''}`}>{b.label}</span>
          )}
        </React.Fragment>
      ))}
      {!loading && (
        <span className="tabular-nums text-neutral-400">
          <span className="mx-1 text-neutral-300">·</span>
          {total} {total === 1 ? 'file' : 'files'}
        </span>
      )}
    </span>
  );

  const renderContent = () => {
    if (loading) {
      return viewMode === 'grid' ? (
        <div className="grid grid-cols-3 gap-0.5 sm:grid-cols-4 sm:gap-2 md:grid-cols-5 xl:grid-cols-6" aria-busy="true">
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <Card divided aria-busy="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5">
              <Skeleton className="h-9 w-9 rounded-lg" />
              <Skeleton className="h-4 w-48" />
            </div>
          ))}
        </Card>
      );
    }

    if (error) {
      return (
        <p className="flex items-center justify-between gap-3 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
          {error}
          <Button size="xs" variant="secondary" onClick={loadAssets}>Retry</Button>
        </p>
      );
    }

    if (assets.length === 0) {
      const filtered = !!(q || typeFilter);
      return (
        <Card>
          <EmptyState
            icon={ImageIcon}
            title={filtered ? 'No matches' : 'No files'}
            className="py-16"
            action={
              !filtered && !showUpload ? (
                <Button size="sm" variant="secondary" onClick={() => setShowUpload(true)}>
                  <Upload className="h-4 w-4" strokeWidth={1.75} />
                  Upload
                </Button>
              ) : undefined
            }
          />
        </Card>
      );
    }

    if (viewMode === 'grid') {
      return (
        <ul className="grid grid-cols-3 gap-0.5 sm:grid-cols-4 sm:gap-2 md:grid-cols-5 xl:grid-cols-6">
          {assets.map(a => {
            const selected = selectedIds.has(a.id);
            return (
              <li key={a.id} className="group relative">
                <button
                  type="button"
                  onClick={() => openOrSelect(a)}
                  title={a.originalName}
                  aria-label={selecting ? `${selected ? 'Deselect' : 'Select'} ${a.originalName}` : a.originalName}
                  className="relative block aspect-square w-full overflow-hidden rounded-xl bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
                >
                  <span
                    className={`absolute inset-0 overflow-hidden transition-transform duration-200 ${
                      selected ? 'scale-[0.9] rounded-lg' : ''
                    }`}
                  >
                    {renderThumb(a)}
                  </span>
                  {/* Name on hover */}
                  {!selecting && (
                    <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-2.5 pb-2 pt-8 text-left opacity-0 transition-opacity group-hover:opacity-100 [@media(hover:none)]:hidden">
                      <span className="block truncate text-[12px] text-white">{a.originalName}</span>
                      <span className="block text-[11px] text-white/70">{mediaService.formatFileSize(a.fileSize)}</span>
                    </span>
                  )}
                </button>

                {/* Select mark */}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => toggleSelect(a.id)}
                    aria-label={`${selected ? 'Deselect' : 'Select'} ${a.originalName}`}
                    aria-pressed={selected}
                    className={`absolute left-1.5 top-1.5 rounded-full p-0.5 transition-opacity focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white ${
                      selecting ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 [@media(hover:none)]:hidden'
                    }`}
                  >
                    <SelectMark selected={selected} />
                  </button>
                )}

                {/* Quick actions */}
                {!selecting && (
                  <span className="absolute right-1.5 top-1.5 flex gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:hidden">
                    <a
                      href={mediaService.getDownloadUrl(a.id)}
                      download={a.originalName}
                      aria-label="Download"
                      title="Download"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-md transition-colors hover:bg-black/60"
                    >
                      <Download className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </a>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(a)}
                        aria-label="Delete"
                        title="Delete"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-md transition-colors hover:bg-primary-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                      </button>
                    )}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      );
    }

    return (
      <Card clip>
        {/* Desktop table */}
        <div className="hidden overflow-x-auto md:block">
          <Table>
            <THead>
              <tr>
                {isAdmin && (
                  <TH className="w-10 pr-0">
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      checked={selectedIds.size === assets.length && assets.length > 0}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 cursor-pointer rounded-[5px] border-0 text-primary-600 ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-primary-600/40 focus:ring-offset-0"
                    />
                  </TH>
                )}
                <TH>Name</TH>
                <TH>Type</TH>
                <TH className="text-right">Size</TH>
                <TH>Entity</TH>
                <TH>Date</TH>
                <TH className="w-24" />
              </tr>
            </THead>
            <TBody>
              {assets.map(a => {
                const selected = selectedIds.has(a.id);
                return (
                  <TR key={a.id} interactive className={`group ${selected ? 'bg-primary-50/40' : ''}`} onClick={() => setViewerAsset(a)}>
                    {isAdmin && (
                      <TD className="pr-0" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${a.originalName}`}
                          checked={selected}
                          onChange={() => toggleSelect(a.id)}
                          className="h-4 w-4 cursor-pointer rounded-[5px] border-0 text-primary-600 ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-primary-600/40 focus:ring-offset-0"
                        />
                      </TD>
                    )}
                    <TD className="max-w-[320px]">
                      <div className="flex items-center gap-3">
                        <span className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-lg bg-neutral-100">{renderThumb(a, true)}</span>
                        <div className="min-w-0">
                          <p className="truncate" title={a.originalName}>{a.originalName}</p>
                          {a.uploader && <p className="truncate text-[12px] text-neutral-400">{a.uploader.email}</p>}
                        </div>
                      </div>
                    </TD>
                    <TD className="capitalize text-neutral-500">{a.type}</TD>
                    <TD className="whitespace-nowrap text-right tabular-nums text-neutral-500">{mediaService.formatFileSize(a.fileSize)}</TD>
                    <TD className="text-neutral-500">
                      <span className="flex max-w-[200px] items-center gap-2">
                        {entityIcon(a)}
                        <span className="min-w-0 truncate">{getEntityName(a)}</span>
                      </span>
                    </TD>
                    <TD className="whitespace-nowrap tabular-nums text-neutral-500">{new Date(a.createdAt).toLocaleDateString()}</TD>
                    <TD onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                        <a
                          href={mediaService.getDownloadUrl(a.id)}
                          download={a.originalName}
                          aria-label="Download"
                          title="Download"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink"
                        >
                          <Download className="h-4 w-4" strokeWidth={1.75} />
                        </a>
                        {isAdmin && (
                          <IconButton label="Delete" size="sm" onClick={() => setDeleteTarget(a)} className="text-neutral-400 hover:bg-primary-50 hover:text-primary-700">
                            <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                          </IconButton>
                        )}
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </div>

        {/* Phone rows */}
        <ul className="divide-y divide-neutral-200/70 md:hidden">
          {assets.map(a => {
            const selected = selectedIds.has(a.id);
            return (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => openOrSelect(a)}
                  className={`flex min-h-[56px] w-full items-center gap-3 px-4 py-2 text-left transition-colors active:bg-neutral-50 ${selected ? 'bg-primary-50/40' : ''}`}
                >
                  {selecting && (
                    <span
                      className={`inline-flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full ${
                        selected ? 'bg-primary-600' : 'ring-[1.5px] ring-inset ring-neutral-300'
                      }`}
                    >
                      {selected && <Check className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />}
                    </span>
                  )}
                  <span className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg bg-neutral-100">{renderThumb(a, true)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] text-ink">{a.originalName}</span>
                    <span className="block truncate text-[12px] text-neutral-400">
                      {mediaService.formatFileSize(a.fileSize)} · {getEntityName(a)}
                    </span>
                  </span>
                  {!selecting && <ChevronRight className="h-4 w-4 flex-shrink-0 text-neutral-300" strokeWidth={1.75} />}
                </button>
              </li>
            );
          })}
        </ul>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <Page wide>
        <PageHeader
          title="Media"
          meta={meta}
          actions={
            <>
              {isAdmin && assets.length > 0 && (
                <Button size="sm" variant={selecting ? 'dark' : 'secondary'} onClick={() => (selecting ? endSelection() : setSelectMode(true))}>
                  {selecting ? 'Done' : 'Select'}
                </Button>
              )}
              <Button size="sm" variant="primary" onClick={() => setShowUpload(p => !p)} aria-expanded={showUpload}>
                {showUpload ? <X className="h-4 w-4" strokeWidth={2} /> : <Upload className="h-4 w-4" strokeWidth={2} />}
                {showUpload ? 'Close' : 'Upload'}
              </Button>
            </>
          }
        />

        <div className="flex gap-5">
          {/* Entity tree */}
          {sidebarOpen && (
            <aside className="hidden w-64 flex-shrink-0 lg:block">
              <div className="sticky top-[88px] h-[calc(100vh-112px)] overflow-hidden rounded-2xl border border-neutral-200 bg-white">
                <EntityTreeSidebar
                  mode="media"
                  selection={selection}
                  onSelect={selectEntity}
                  entityCounts={entityCounts}
                  totalCount={totalMediaCount}
                />
              </div>
            </aside>
          )}

          <div className="min-w-0 flex-1">
            {showUpload && (
              <div className="mb-3 animate-fade-up">
                <FileUpload
                  options={{
                    stakeholderId: selection.type === 'stakeholder' ? selection.id : undefined,
                    organizationId: selection.type === 'organization' ? selection.id : undefined,
                  }}
                  multiple
                  onUploadComplete={() => { loadAssets(); loadCounts(); }}
                />
              </div>
            )}

            {/* Toolbar */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <IconButton
                label={sidebarOpen ? 'Hide folders' : 'Show folders'}
                onClick={() => setSidebarOpen(p => !p)}
                className="hidden lg:inline-flex"
              >
                <PanelLeft className="h-[18px] w-[18px]" strokeWidth={1.75} />
              </IconButton>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setTreeSheetOpen(true)}
                className="h-10 max-w-[45%] sm:h-8 lg:hidden"
                title="Folders"
              >
                <FolderTree className="h-4 w-4 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
                <span className="truncate">{selectionLabel}</span>
              </Button>
              <SearchInput
                className="min-w-0 flex-1 sm:min-w-[140px] sm:max-w-xs"
                placeholder="Search"
                aria-label="Search files"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
              {/* Sort and view: one menu on phones */}
              <Menu
                className="sm:hidden"
                trigger={props => (
                  <IconButton label="Sort and view" className="h-10 w-10" {...props}>
                    <ArrowUpDown className="h-[18px] w-[18px]" strokeWidth={1.75} />
                  </IconButton>
                )}
                items={[
                  ...sortItems,
                  { label: 'Grid', icon: viewMode === 'grid' ? Check : LayoutGrid, onSelect: () => setViewMode('grid') },
                  { label: 'List', icon: viewMode === 'list' ? Check : List, onSelect: () => setViewMode('list') },
                ]}
              />

              <SegmentedControl<TypeFilter>
                label="File type"
                items={TYPE_ITEMS}
                value={typeFilter}
                onChange={value => refilter({ type: value || null })}
                className="w-full sm:w-auto"
              />

              <div className="hidden items-center gap-1 sm:flex">
                <Menu
                  trigger={props => (
                    <IconButton label={`Sort: ${SORT_OPTIONS.find(o => o.value === sort)?.label}`} {...props}>
                      <ArrowUpDown className="h-4 w-4" strokeWidth={1.75} />
                    </IconButton>
                  )}
                  items={sortItems}
                />
                <SegmentedControl<ViewMode>
                  label="View"
                  value={viewMode}
                  onChange={setViewMode}
                  items={[
                    { value: 'grid', label: <LayoutGrid className="h-4 w-4" strokeWidth={1.75} />, title: 'Grid' },
                    { value: 'list', label: <List className="h-4 w-4" strokeWidth={1.75} />, title: 'List' },
                  ]}
                />
              </div>
            </div>

            {renderContent()}

            {/* Pagination */}
            {assets.length > 0 && !loading && (
              <div className="mt-4 flex items-center justify-between gap-3 text-[12px] text-neutral-500">
                <span className="tabular-nums">
                  {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
                </span>
                <div className="flex items-center gap-1">
                  <Select
                    aria-label="Per page"
                    value={limit}
                    onChange={e => refilter({ limit: e.target.value === '24' ? null : e.target.value })}
                    className="mr-2 w-[76px]"
                  >
                    <option value={24}>24</option>
                    <option value={48}>48</option>
                    <option value={96}>96</option>
                  </Select>
                  <IconButton label="Previous" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
                  </IconButton>
                  <span className="min-w-[44px] text-center tabular-nums">{page} / {totalPages}</span>
                  <IconButton label="Next" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                    <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
                  </IconButton>
                </div>
              </div>
            )}
          </div>
        </div>
      </Page>

      {/* Bulk action bar */}
      {hasBulkBar && (
        <div
          className="pointer-events-none fixed inset-x-0 z-40 flex justify-center px-4 pb-4 font-ui sm:pb-6"
          style={{ bottom: 'var(--app-tabbar-h, 0px)' }}
        >
          <div
            role="toolbar"
            aria-label="Selection"
            className="pointer-events-auto flex h-12 w-full max-w-md animate-fade-up items-center justify-between gap-2 rounded-full bg-ink pl-5 pr-1.5 text-white shadow-[0_20px_48px_-16px_rgba(0,0,0,0.45)] ring-1 ring-white/10 sm:w-auto sm:min-w-[380px]"
          >
            <p className="min-w-0 truncate text-[13px] tabular-nums text-neutral-300" aria-live="polite">
              <span className="text-white">{selectedIds.size}</span> selected
            </p>
            <div className="flex flex-shrink-0 items-center gap-1">
              {selectedIds.size < assets.length && (
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="inline-flex h-9 items-center rounded-full px-3 text-[13px] text-neutral-300 transition-colors hover:bg-white/10 hover:text-white"
                >
                  All
                </button>
              )}
              <button
                type="button"
                onClick={endSelection}
                aria-label="Clear"
                className="inline-flex h-9 items-center gap-1.5 rounded-full px-2.5 text-[13px] text-neutral-400 transition-colors hover:bg-white/10 hover:text-white sm:px-3"
              >
                <X className="h-4 w-4" strokeWidth={1.75} />
                <span className="hidden sm:inline">Clear</span>
              </button>
              <button
                type="button"
                onClick={() => setBulkConfirmOpen(true)}
                className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary-600 px-4 text-[13px] font-medium text-white transition-colors hover:bg-primary-700"
              >
                <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {actionMessage && (
        <div
          className="pointer-events-none fixed inset-x-0 z-40 flex justify-center px-4 font-ui"
          style={{ bottom: `calc(var(--app-tabbar-h, 0px) + ${hasBulkBar ? '5.5rem' : '1.25rem'})` }}
          role="status"
        >
          <span className="inline-flex h-9 animate-fade-up items-center gap-2 rounded-full bg-ink px-4 text-[13px] text-white shadow-[0_12px_32px_-12px_rgba(0,0,0,0.4)]">
            <CircleCheck className="h-4 w-4 text-emerald-400" strokeWidth={1.75} />
            {actionMessage}
          </span>
        </div>
      )}

      {/* Entity tree as a sheet below lg */}
      <Dialog
        open={treeSheetOpen}
        onClose={() => setTreeSheetOpen(false)}
        labelledBy="media-folders-title"
        handle
        className="h-[80dvh] sm:h-[70vh] sm:max-w-sm"
      >
        <DialogHeader id="media-folders-title" title="Folders" onClose={() => setTreeSheetOpen(false)} className="pb-3 max-sm:pt-1" />
        <div className="min-h-0 flex-1 overflow-hidden border-t border-neutral-200/70">
          <EntityTreeSidebar
            mode="media"
            selection={selection}
            onSelect={selectEntity}
            entityCounts={entityCounts}
            totalCount={totalMediaCount}
          />
        </div>
      </Dialog>

      {/* Delete confirm */}
      <ConfirmModal
        open={!!deleteTarget}
        title="Delete file"
        message={<span>Delete “{deleteTarget?.originalName}”? This can’t be undone.</span>}
        confirmText="Delete"
        loading={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
      <ConfirmModal
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.size} ${selectedIds.size === 1 ? 'file' : 'files'}`}
        message="This can’t be undone."
        confirmText="Delete"
        loading={bulkDeleting}
        onCancel={() => setBulkConfirmOpen(false)}
        onConfirm={handleBulkDelete}
      />

      {/* Fullscreen media viewer */}
      <MediaViewer
        media={viewerAsset}
        onClose={() => setViewerAsset(null)}
        allowDelete={!!isAdmin}
        onDelete={(deleted) => {
          setAssets(prev => prev.filter(a => a.id !== deleted.id));
          setTotal(prev => prev - 1);
          loadCounts();
          flash('File deleted');
        }}
        hasPrev={viewerAsset ? assets.indexOf(viewerAsset) > 0 : false}
        hasNext={viewerAsset ? assets.indexOf(viewerAsset) < assets.length - 1 : false}
        onNavigate={(dir) => {
          if (!viewerAsset) return;
          const idx = assets.indexOf(viewerAsset);
          if (dir === 'prev' && idx > 0) setViewerAsset(assets[idx - 1]);
          if (dir === 'next' && idx < assets.length - 1) setViewerAsset(assets[idx + 1]);
        }}
      />
    </DashboardLayout>
  );
};
